import { useEffect, useState } from "react";

import { Checkbox, InputGroup } from "@blueprintjs/core";
import { addHours, isAfter, isBefore, isSameDay } from "date-fns";
import Select from "react-select";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import { detectMeetingPlatform } from "@/components/pages_logged_in/program/components/utils";
import { SUBTEXT_COLOR } from "@/css/constants";
import { CustomFormField, MeetingPlatforms } from "@/types/programs";

import {
  ALL_MEETING_PLATFORMS,
  getMeetingPlatformCreateHelpLink,
  getMeetingPlatformIcon,
} from "./utils";

/**
 * Props for the MeetingLinkFormInput component
 * Generated by Copilot
 */
interface MeetingLinkFormInputProps {
  customFormField: CustomFormField;
  onChange: (customFormField: Partial<CustomFormField>) => void;
  saving?: boolean;
  isEdit?: boolean;
  onIsSendMeetingUpdateEmailChange?: (
    isSendMeetingUpdateEmail: boolean,
  ) => void;
  targetStartDate: string | null;
  targetEndDate: string | null;
}

/**
 * Component for creating and editing meeting links
 * Generated by Copilot
 */
export function MeetingLinkFormInput({
  customFormField,
  saving = false,
  onChange,
  isEdit = false,
  onIsSendMeetingUpdateEmailChange,
  targetStartDate,
  targetEndDate,
}: MeetingLinkFormInputProps) {
  const [meetingPlatform, setMeetingPlatform] = useState<MeetingPlatforms>();
  const [isSendMeetingUpdateEmail, setIsSendMeetingUpdateEmail] =
    useState<boolean>(false);
  const [
    isShowMeetingUpdateEmailCheckbox,
    setIsShowMeetingUpdateEmailCheckbox,
  ] = useState<boolean>(false);
  const [initialLink, setInitialLink] = useState<string>();
  const [initStartDate, setInitStartDate] = useState<string | null>(null);
  const [initEndDate, setInitEndDate] = useState<string | null>(null);
  /**
   * Detect meeting platform from link when component mounts or customFormField changes
   * Generated by Copilot
   */
  useEffect(() => {
    if (customFormField?.link) {
      setMeetingPlatform(detectMeetingPlatform(customFormField.link));
      if (isEdit) {
        if (!initialLink) {
          setInitialLink(customFormField.link);
        }
        if (!initStartDate) {
          setInitStartDate(targetStartDate);
        }
        if (!initEndDate) {
          setInitEndDate(targetEndDate);
        }
      }
    }
    return () => {};
  }, [customFormField, targetStartDate, isEdit]);

  const helpCreateLink = getMeetingPlatformCreateHelpLink(meetingPlatform);

  /**
   * Options for meeting platform select dropdown
   * Generated by Copilot
   */
  const meetingPlatformOptions = ALL_MEETING_PLATFORMS.map((platform) => ({
    value: platform,
    label: (
      <Flex alignItems="center" style={{ gap: 8 }}>
        <EmbedSvg
          src={getMeetingPlatformIcon(platform)}
          height={18}
          width={18}
        />
        {platform}
      </Flex>
    ),
  }));

  /**
   * Handle meeting link change
   * Generated by Copilot
   */
  const handleMeetingChange = (value: string): void => {
    // Check if link has changed compared to the previous value
    if (isEdit) {
      const hasChanged =
        initialLink !== value ||
        (initStartDate &&
          targetStartDate &&
          !isSameDay(new Date(initStartDate), new Date(targetStartDate))) ||
        (initEndDate &&
          targetEndDate &&
          !isSameDay(new Date(initEndDate), new Date(targetEndDate)));
      const currentTime = new Date();
      const next24Hours = addHours(currentTime, 24);

      const isAtLeast24HoursBeforeMeeting =
        initStartDate &&
        targetStartDate &&
        isBefore(new Date(initStartDate), next24Hours);

      const isEnded =
        initEndDate &&
        targetEndDate &&
        isAfter(currentTime, new Date(initEndDate));

      const shouldShowMeetingUpdateEmailCheckbox = Boolean(
        hasChanged && isAtLeast24HoursBeforeMeeting && !isEnded,
      );
      setIsShowMeetingUpdateEmailCheckbox(shouldShowMeetingUpdateEmailCheckbox);
      // If the checkbox is not yet set, set it to the value of shouldShowReminderEmailCheckbox
    }

    onChange({
      link: value,
      prompt: "Group Meeting Component",
    });
  };

  /**
   * Handle reminder email checkbox change
   */
  const handleIsSendMeetingUpdateEmailChange = (
    event: React.FormEvent<HTMLInputElement>,
  ): void => {
    const isChecked = event.currentTarget.checked;
    setIsSendMeetingUpdateEmail(isChecked);
  };

  useEffect(() => {
    onIsSendMeetingUpdateEmailChange?.(!!isSendMeetingUpdateEmail);
  }, [isSendMeetingUpdateEmail]);

  useEffect(() => {
    handleMeetingChange(customFormField.link || "");
  }, [targetStartDate, targetEndDate]);

  return (
    <>
      <StyledFormGroup label={"Meeting Platform"}>
        <Select
          options={meetingPlatformOptions}
          value={meetingPlatformOptions.find(
            (o) => o.value === meetingPlatform,
          )}
          onChange={(e) => {
            setMeetingPlatform(e?.value ?? undefined);
          }}
        />
      </StyledFormGroup>
      <StyledFormGroup label={"Meeting Link"}>
        <InputGroup
          style={{ fontSize: 16 }}
          value={customFormField?.link || ""}
          onChange={(e) => handleMeetingChange(e.target.value)}
          large={true}
          fill={true}
          placeholder={"https://"}
          disabled={saving}
        />
        {helpCreateLink && (
          <Box mt={2} color={SUBTEXT_COLOR} fontSize={14}>
            Need to create {meetingPlatform} meeting?{" "}
            <a href={helpCreateLink} target="_blank" rel="noopener noreferrer">
              Create {meetingPlatform} meeting
            </a>
          </Box>
        )}
        {customFormField?.link &&
          !isValidHttpOrHttpsUrl(customFormField.link) && (
            <Box color={"#c23030"} fontSize={14} mt={10}>
              Invalid URL
            </Box>
          )}

        {isShowMeetingUpdateEmailCheckbox && (
          <Checkbox
            style={{ marginTop: 20 }}
            checked={isSendMeetingUpdateEmail}
            onChange={handleIsSendMeetingUpdateEmailChange}
            label="Send reminder email to participants about the updated meeting"
          />
        )}
      </StyledFormGroup>

      <Box
        px={16}
        py={12}
        color={"#215DB0"}
        css={{
          backgroundColor: "#E9F1FB",
          borderRadius: 4,
        }}
        fontSize={14}
      >
        When this program goes live, participants will:
        <ul
          className="list-disc"
          style={{
            paddingInlineStart: 24,
            marginTop: 4,
          }}
        >
          <li>See this session in their program timeline</li>
          <li>Receive a notification about the session</li>
        </ul>
      </Box>
    </>
  );
}
